import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusBadge, PriorityBadge, TicketIdBadge } from "@/components/StatusBadge";
import { Trash2, RotateCcw, AlertTriangle, ArrowLeft } from "lucide-react";

export default function Trash() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets/trash");
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trash");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    try {
      await api.post(`/tickets/${t.ticket_id}/restore`);
      toast.success(`${t.ticket_number} restored`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const purge = async (t) => {
    if (!window.confirm(`Permanently purge ${t.ticket_number} — "${t.title}"?\n\nThis cannot be undone. All comments, attachments and history will be wiped.`)) return;
    try {
      await api.delete(`/tickets/${t.ticket_id}/purge`);
      toast.success(`${t.ticket_number} purged forever`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    }
  };

  const restoreAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Restore all ${items.length} ticket(s) from the trash?`)) return;
    try {
      const { data } = await api.post("/tickets/trash/restore-all");
      toast.success(`Restored ${data.restored} ticket(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore all failed");
    }
  };

  const purgeAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Permanently purge all ${items.length} trashed ticket(s)?\n\nThis cannot be undone. Every comment, attachment and history entry will be wiped.`)) return;
    try {
      const { data } = await api.delete("/tickets/trash/purge-all");
      toast.success(`Purged ${data.purged} ticket(s) forever`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Empty trash failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Link to="/tickets" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 font-mono uppercase tracking-[0.2em]" data-testid="trash-back">
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> All Tickets
          </Link>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-400 mt-3">Recycle</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2" data-testid="trash-heading">Trash Bin</h1>
          <p className="text-sm text-slate-500 mt-2">
            Soft-deleted tickets are kept here so admins can restore or permanently purge them.
            Anything older than 30 days is auto-purged nightly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={restoreAll}
            disabled={items.length === 0}
            className="border-emerald-500/40 bg-transparent hover:bg-emerald-500/10 text-emerald-300 hover:text-emerald-200 rounded-sm disabled:opacity-40"
            data-testid="restore-all-btn"
          >
            <RotateCcw className="h-4 w-4" strokeWidth={1.5} /> Restore all
          </Button>
          <Button
            variant="outline"
            onClick={purgeAll}
            disabled={items.length === 0}
            className="border-red-500/50 bg-transparent hover:bg-red-500/10 text-red-300 hover:text-red-200 rounded-sm disabled:opacity-40"
            data-testid="empty-trash-btn"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Empty trash
          </Button>
          <Button
            variant="outline"
            onClick={load}
            className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm"
            data-testid="trash-refresh"
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="border border-red-500/30 bg-red-500/5 p-3 rounded-sm flex items-center gap-2 text-xs text-red-200" data-testid="trash-warning">
        <AlertTriangle className="h-4 w-4" strokeWidth={1.5} />
        Purge is permanent. Restore before purging if you&apos;re not 100% sure.
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <div className="col-span-3">Ticket</div>
          <div className="col-span-2">Reporter</div>
          <div className="col-span-2">Deleted by</div>
          <div className="col-span-2">Deleted at</div>
          <div className="col-span-1">Priority</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="divide-y divide-slate-800">
          {loading && (
            <div className="p-10 text-center text-xs font-mono uppercase tracking-[0.3em] text-slate-500" data-testid="trash-loading">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="trash-empty">
              Trash is empty — nothing to restore or purge.
            </div>
          )}
          {items.map((t) => (
            <div
              key={t.ticket_id}
              className="grid grid-cols-12 gap-3 items-center px-5 py-3 hover:bg-slate-100 transition-colors"
              data-testid={`trash-row-${t.ticket_number}`}
            >
              <div className="col-span-3">
                <div className="flex items-center gap-2 mb-1">
                  <TicketIdBadge id={t.ticket_number} />
                  <StatusBadge status={t.status} />
                </div>
                <div className="text-sm truncate">{t.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{t.category_name || "Uncategorized"}</div>
              </div>
              <div className="col-span-2 truncate">
                <div className="text-sm text-slate-700 truncate">{t.reporter_name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.organization || "—"}</div>
              </div>
              <div className="col-span-2 text-sm text-slate-700 truncate">
                {t.deleted_by_name || <span className="text-slate-500 font-mono text-xs">{t.deleted_by || "—"}</span>}
              </div>
              <div className="col-span-2 text-xs font-mono text-slate-600 truncate">
                {t.deleted_at ? new Date(t.deleted_at).toLocaleString() : "—"}
              </div>
              <div className="col-span-1">
                <PriorityBadge priority={t.priority} />
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => restore(t)}
                  className="border-emerald-500/40 bg-transparent hover:bg-emerald-500/10 text-emerald-300 hover:text-emerald-200 rounded-sm text-xs h-8"
                  data-testid={`restore-${t.ticket_number}`}
                >
                  <RotateCcw className="h-3 w-3" strokeWidth={1.5} /> Restore
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => purge(t)}
                  className="border-red-500/50 bg-transparent hover:bg-red-500/10 text-red-300 hover:text-red-200 rounded-sm text-xs h-8"
                  data-testid={`purge-${t.ticket_number}`}
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Purge
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
